import { AppLayout } from '@/components/AppLayout';
import { HIRE_PAGE_CONTAINER, HIRE_SECTION_SPACING } from '@/lib/hireResponsive';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useOrganization, useUpdateOrganization, useOrgSensitiveFields } from '@/hooks/useOrganization';
import { useAuth } from '@/contexts/AuthContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useState, useEffect } from 'react';
import { Phone, Mail, User, Lock, UserCog, KeyRound, MailCheck, Zap, Languages } from 'lucide-react';
import EmailTemplatesManager from '@/components/email/EmailTemplatesManager';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import {
  getCanonicalPortalPasswordStatus,
  protectedPasswordLogin,
  resolvePasswordReauthenticationVisibleEmail,
} from '@/lib/protectedPublicAuth';
import { initiateGovernedEmailChange } from '@/lib/governedEmailChange';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { data: org, isLoading: orgLoading } = useOrganization();
  const { data: sensitiveFields } = useOrgSensitiveFields();
  const { role, user } = useAuth();
  const updateOrg = useUpdateOrganization();
  const { toast } = useToast();
  const navigate = useNavigate();

  const isAdmin = role === 'org_owner' || role === 'org_admin';

  // Login email change
  const [loginEmail, setLoginEmail] = useState('');
  const [origLoginEmail, setOrigLoginEmail] = useState('');

  const [contactName, setContactName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const [origContactName, setOrigContactName] = useState('');
  const [origContactNumber, setOrigContactNumber] = useState('');
  const [origContactEmail, setOrigContactEmail] = useState('');

  // Automation
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [autoSendInvite, setAutoSendInvite] = useState(true);
  const [autoAdvanceSignedContracts, setAutoAdvanceSignedContracts] = useState(false);
  const [communicationLanguage, setCommunicationLanguage] = useState<'en' | 'ar'>('en');
  const [savingCommunicationLanguage, setSavingCommunicationLanguage] = useState(false);

  // Password confirmation dialog
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [dialogPurpose, setDialogPurpose] = useState<'contact' | 'email'>('contact');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [hasForkPassword, setHasForkPassword] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    void getCanonicalPortalPasswordStatus(supabase, 'hire').then((result) => {
      if (active && !result.error) setHasForkPassword(result.hasPassword);
    });
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    if (user?.email) {
      setLoginEmail(user.email);
      setOrigLoginEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;

    let active = true;
    supabase
      .from('hire_profiles')
      .select('communication_language')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!active) return;
        setCommunicationLanguage(data?.communication_language === 'ar' ? 'ar' : 'en');
      });

    return () => {
      active = false;
    };
  }, [user?.id]);

  useEffect(() => {
    if (org) {
      setAutoAdvance(org.auto_advance_to_interview ?? true);
      setAutoSendInvite(org.auto_send_interview_invite ?? true);
      setAutoAdvanceSignedContracts(org.auto_advance_signed_contracts_to_hired ?? false);
    }
  }, [org]);

  useEffect(() => {
    if (sensitiveFields) {
      const cn = sensitiveFields.contact_name || '';
      const cnum = sensitiveFields.contact_number || '';
      const ce = sensitiveFields.contact_email || '';
      setContactName(cn);
      setContactNumber(cnum);
      setContactEmail(ce);
      setOrigContactName(cn);
      setOrigContactNumber(cnum);
      setOrigContactEmail(ce);
    }
  }, [sensitiveFields]);

  const hasContactChanges = contactName !== origContactName || contactNumber !== origContactNumber || contactEmail !== origContactEmail;
  const hasEmailChange = loginEmail !== origLoginEmail;

  const handleSaveContactClick = () => {
    if (!hasContactChanges) return;
    setConfirmPassword('');
    setDialogPurpose('contact');
    setShowPasswordDialog(true);
  };

  const handleSaveEmailClick = () => {
    if (!hasEmailChange) return;
    if (!loginEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginEmail)) {
      toast({ title: t('hire.settings.invalidEmail'), description: t('hire.settings.invalidEmailDesc'), variant: 'destructive' });
      return;
    }
    setConfirmPassword('');
    setDialogPurpose('email');
    setShowPasswordDialog(true);
  };

  const verifyPassword = async (): Promise<boolean> => {
    if (!confirmPassword.trim()) {
      toast({ title: t('hire.settings.passwordRequired'), description: t('hire.settings.passwordRequiredDesc'), variant: 'destructive' });
      return false;
    }

    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const visibleEmail = currentUser ? resolvePasswordReauthenticationVisibleEmail(currentUser) : '';
    if (!currentUser?.email || !visibleEmail) {
      toast({ title: t('hire.account.error'), description: t('hire.account.verifyIdentityFailed'), variant: 'destructive' });
      return false;
    }

    const { error: authError } = await protectedPasswordLogin(supabase, {
      portal: 'hire',
      email: currentUser.email,
      visibleEmail,
      password: confirmPassword,
    });

    if (authError) {
      toast({ title: t('hire.settings.incorrectPassword'), description: t('hire.settings.incorrectPasswordDesc'), variant: 'destructive' });
      return false;
    }

    return true;
  };

  const handleConfirmSave = async () => {
    setVerifying(true);

    const verified = await verifyPassword();
    if (!verified) {
      setVerifying(false);
      return;
    }

    if (dialogPurpose === 'email') {
      const { error } = await initiateGovernedEmailChange(supabase, {
        portal: 'hire',
        newEmail: loginEmail,
        confirmPassword,
      });
      if (error) {
        toast({ title: t('hire.settings.emailUpdateFailed'), description: error.message, variant: 'destructive' });
      } else {
        toast({
          title: t('hire.settings.confirmationSent'),
          description: t('hire.settings.confirmationSentDesc'),
        });
        setOrigLoginEmail(loginEmail);
        setShowPasswordDialog(false);
        setConfirmPassword('');
      }
      setVerifying(false);
    } else {
      if (org) {
        updateOrg.mutate(
          { id: org.id, contact_name: contactName, contact_number: contactNumber, contact_email: contactEmail },
          {
            onSuccess: () => {
              setOrigContactName(contactName);
              setOrigContactNumber(contactNumber);
              setOrigContactEmail(contactEmail);
              setShowPasswordDialog(false);
              setConfirmPassword('');
            },
            onSettled: () => setVerifying(false),
          }
        );
      } else {
        setVerifying(false);
      }
    }
  };

  const handleAutoAdvanceToggle = (checked: boolean) => {
    setAutoAdvance(checked);
    if (org) {
      updateOrg.mutate({ id: org.id, auto_advance_to_interview: checked });
    }
  };

  const handleAutoSendInviteToggle = (checked: boolean) => {
    setAutoSendInvite(checked);
    if (org) {
      updateOrg.mutate({ id: org.id, auto_send_interview_invite: checked });
    }
  };

  const handleAutoAdvanceSignedContractsToggle = (checked: boolean) => {
    setAutoAdvanceSignedContracts(checked);
    if (org) {
      updateOrg.mutate({ id: org.id, auto_advance_signed_contracts_to_hired: checked });
    }
  };

  const handleCommunicationLanguageChange = async (value: 'en' | 'ar') => {
    if (!user?.id) return;
    setCommunicationLanguage(value);
    setSavingCommunicationLanguage(true);

    const { error } = await supabase
      .from('hire_profiles')
      .update({ communication_language: value })
      .eq('user_id', user.id);

    setSavingCommunicationLanguage(false);
    if (error) {
      toast({ title: t('hire.settings.communicationLanguageFailed'), description: error.message, variant: 'destructive' });
      return;
    }

    toast({ title: t('hire.settings.communicationLanguageUpdated') });
  };

  return (
    <AppLayout>
      <div className={`${HIRE_SECTION_SPACING} ${HIRE_PAGE_CONTAINER}`}>
        <Card className="shadow-card border-border border-t-4 border-t-primary overflow-hidden">
          <div className="p-5 sm:p-6">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-display text-foreground">{t('hire.settings.title')}</h1>
            <p className="text-muted-foreground mt-1.5 text-sm">{t('hire.settings.pageSubtitle')}</p>
          </div>
        </Card>

        <Tabs defaultValue="account" className="space-y-5">
          <TabsList className="rounded-xl">
            <TabsTrigger value="account" className="gap-1.5 rounded-lg touch-target">
              <UserCog className="w-4 h-4" /> {t('hire.settings.account')}
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-1.5 rounded-lg touch-target">
              <MailCheck className="w-4 h-4" /> {t('hire.settings.emailTemplates')}
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="automation" className="gap-1.5 rounded-lg touch-target">
                <Zap className="w-4 h-4" /> {t('hire.settings.automation')}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Account Info */}
          <TabsContent value="account">
            {orgLoading ? (
              <div className="space-y-3"><Skeleton className="h-12 w-full rounded-xl" /><Skeleton className="h-12 w-full rounded-xl" /><Skeleton className="h-12 w-full rounded-xl" /></div>
            ) : (
              <div className="space-y-5">
                {/* Login & Security */}
                <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Lock className="w-5 h-5 text-accent" />
                    <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.settings.loginSecurity')}</h2>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-3">
                    {t('hire.settings.loginSecurityDesc')}
                  </p>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.loginEmail')}
                    </label>
                    <Input
                      type="email"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      className="h-11 rounded-xl"
                    />
                    <p className="text-xs text-muted-foreground">{t('hire.settings.loginEmailHint')}</p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      className="gradient-accent text-accent-foreground hover:opacity-90 touch-target h-10 px-5 rounded-xl"
                      onClick={handleSaveEmailClick}
                      disabled={!hasEmailChange}
                    >
                      {t('hire.settings.updateEmail')}
                    </Button>
                    <Button
                      variant="outline"
                      className="touch-target h-10 px-5 rounded-xl gap-1.5"
                      onClick={() => navigate('/change-password')}
                    >
                      <KeyRound className="w-4 h-4" />
                      {t(hasForkPassword === false ? 'hire.settings.setForkPassword' : 'hire.settings.changePassword')}
                    </Button>
                  </div>
                  {hasForkPassword === false ? (
                    <p className="text-xs text-muted-foreground">{t('hire.settings.setForkPasswordDesc')}</p>
                  ) : null}
                </Card>

                <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Languages className="w-5 h-5 text-accent" />
                    <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.settings.communication')}</h2>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="communication-language" className="text-sm font-medium text-foreground">
                      {t('hire.settings.communicationLanguage')}
                    </Label>
                    <Select
                      value={communicationLanguage}
                      onValueChange={(value) => handleCommunicationLanguageChange(value as 'en' | 'ar')}
                      disabled={savingCommunicationLanguage}
                    >
                      <SelectTrigger id="communication-language" className="h-11 rounded-xl">
                        <SelectValue placeholder={t('hire.settings.languageEnglish')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">{t('hire.settings.languageEnglish')}</SelectItem>
                        <SelectItem value="ar">{t('hire.settings.languageArabic')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {t('hire.settings.communicationLanguageHint')}
                    </p>
                  </div>
                </Card>

                {/* Notification Contact */}
                <Card className="p-6 sm:p-8 shadow-card border-border space-y-5 rounded-xl">
                  <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.settings.notificationContact')}</h2>
                  <p className="text-xs text-muted-foreground -mt-3">
                    {t('hire.settings.notificationContactDesc')} {!isAdmin && t('hire.settings.adminOnly')}
                  </p>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.contactName')}
                      </label>
                      <Input
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        disabled={!isAdmin}
                        placeholder={t('hire.settings.primaryContactPlaceholder')}
                        className={`h-11 rounded-xl ${!isAdmin ? 'bg-muted/50 cursor-not-allowed' : ''}`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.contactNumber')}
                      </label>
                      <Input
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        disabled={!isAdmin}
                        placeholder="+966 5XXXXXXXX"
                        className={`h-11 rounded-xl ${!isAdmin ? 'bg-muted/50 cursor-not-allowed' : ''}`}
                      />
                      <p className="text-xs text-muted-foreground">{t('hire.settings.contactNumberHint')}</p>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-muted-foreground" /> {t('hire.settings.contactEmail')}
                      </label>
                      <Input
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        disabled={!isAdmin}
                        placeholder="notifications@company.com"
                        className={`h-11 rounded-xl ${!isAdmin ? 'bg-muted/50 cursor-not-allowed' : ''}`}
                      />
                      <p className="text-xs text-muted-foreground">{t('hire.settings.contactEmailHint')}</p>
                    </div>
                  </div>

                  {isAdmin && (
                    <Button
                      className="gradient-accent text-accent-foreground hover:opacity-90 touch-target h-10 px-5 rounded-xl"
                      onClick={handleSaveContactClick}
                      disabled={!hasContactChanges || updateOrg.isPending}
                    >
                      {updateOrg.isPending ? t('hire.account.saving') : t('hire.settings.saveContactInfo')}
                    </Button>
                  )}
                </Card>
              </div>
            )}
          </TabsContent>

          <TabsContent value="emails">
            <EmailTemplatesManager />
          </TabsContent>

          {/* Automation Tab - Admin only */}
          {isAdmin && (
            <TabsContent value="automation">
              {orgLoading ? (
                <div className="space-y-3"><Skeleton className="h-12 w-full rounded-xl" /><Skeleton className="h-12 w-full rounded-xl" /></div>
              ) : (
                <Card className="p-6 sm:p-8 shadow-card border-border space-y-6 rounded-xl">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-5 h-5 text-accent" />
                    <h2 className="font-display font-semibold text-foreground text-lg">{t('hire.settings.pipelineAutomation')}</h2>
                  </div>
                  <p className="text-xs text-muted-foreground -mt-3">
                    {t('hire.settings.pipelineAutomationDesc')}
                  </p>

                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border bg-muted/30">
                    <div className="space-y-1.5 flex-1">
                      <Label htmlFor="auto-advance" className="text-sm font-medium text-foreground">
                        {t('hire.settings.autoAdvance')}
                      </Label>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t('hire.settings.autoAdvanceDesc')}
                      </p>
                    </div>
                    <Switch
                      id="auto-advance"
                      checked={autoAdvance}
                      onCheckedChange={handleAutoAdvanceToggle}
                      disabled={updateOrg.isPending}
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border bg-muted/30">
                    <div className="space-y-1.5 flex-1">
                      <Label htmlFor="auto-send-invite" className="text-sm font-medium text-foreground">
                        {t('hire.settings.autoSendInvite')}
                      </Label>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t('hire.settings.autoSendInviteDesc')}
                      </p>
                    </div>
                    <Switch
                      id="auto-send-invite"
                      checked={autoSendInvite}
                      onCheckedChange={handleAutoSendInviteToggle}
                      disabled={updateOrg.isPending}
                    />
                  </div>

                  <div className="flex items-start justify-between gap-4 p-4 rounded-xl border border-border bg-muted/30">
                    <div className="space-y-1.5 flex-1">
                      <Label htmlFor="auto-advance-signed-contracts" className="text-sm font-medium text-foreground">
                        {t('hire.settings.autoAdvanceSignedContracts')}
                      </Label>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {t('hire.settings.autoAdvanceSignedContractsDesc')}
                      </p>
                    </div>
                    <Switch
                      id="auto-advance-signed-contracts"
                      checked={autoAdvanceSignedContracts}
                      onCheckedChange={handleAutoAdvanceSignedContractsToggle}
                      disabled={updateOrg.isPending}
                    />
                  </div>

                  <div className="text-xs text-muted-foreground bg-muted/20 rounded-lg p-3 space-y-1">
                    <p className="font-medium text-foreground">{t('hire.settings.howItWorks')}</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li><strong>{t('hire.settings.howCvScoredTitle')}:</strong> {t('hire.settings.howCvScoredDesc')}</li>
                      <li><strong>{t('hire.settings.howAiReviewedTitle')}:</strong> {t('hire.settings.howAiReviewedDesc')}</li>
                      <li><strong>{t('hire.settings.howShortlistedTitle')}:</strong> {t('hire.settings.howShortlistedDesc')}</li>
                      <li><strong>{t('hire.settings.howSigningContractTitle')}:</strong> {t('hire.settings.howSigningContractDesc')}</li>
                    </ul>
                  </div>
                </Card>
              )}
            </TabsContent>
          )}

        </Tabs>
      </div>

      {/* Password Confirmation Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">{t('hire.settings.confirmIdentity')}</DialogTitle>
            <DialogDescription>
              {dialogPurpose === 'email'
                ? t('hire.settings.confirmEmailChange')
                : t('hire.settings.confirmAccountChange')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <label className="text-sm font-medium text-foreground">{t('hire.settings.password')}</label>
            <div className="relative">
              <Lock className="absolute start-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="ps-10 h-11 rounded-xl"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') handleConfirmSave(); }}
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-xl" onClick={() => setShowPasswordDialog(false)} disabled={verifying}>
              {t('common.cancel')}
            </Button>
            <Button className="gradient-accent text-accent-foreground hover:opacity-90 rounded-xl" onClick={handleConfirmSave} disabled={verifying}>
              {verifying ? t('hire.account.verifying') : t('hire.account.confirmAndSave')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
